import React from "react";

const PrivacyPolicy = () => {
  return (
    <section className="w-full bg-primary text-white relative">
      <div className="max-w-6xl mx-auto px-4 py-16 pt-32 md:pt-40 flex flex-col md:flex-row items-center">
        <h1 className="text-brandAccent text-4xl md:text-5xl font-extrabold mb-4">
          Privacy Policy
        </h1>
      </div>
      <div className="px-4 md:px-12 py-32 rounded-tl-3xl bg-white text-primary">
        <h2 className="font-bold text-lg md:text-2xl my-8">
          PURPOSE OF THIS PRIVACY POLICY
        </h2>
        <p className="mb-5 text-base md:text-lg">
          This Privacy Policy describes how we will collect, use, share and
          otherwise process your personal data in connection with your use of:
        </p>
        <ul className="list-disc pl-5 space-y-2 mb-5 text-base md:text-lg">
          <li>The Zorqk mobile application software (the “App”).</li>
          <li>
            Any of our services that are accessible through the App or on our
            website at: https://zorqk.com or associated websites (the
            “Services”).
          </li>
        </ul>
        <p className="mb-5 text-base md:text-lg">
          This App is not intended for children, and we do not knowingly collect
          data relating to children.
        </p>
        <p className="mb-5 text-base md:text-lg">
          Please read the following carefully to understand our practices
          regarding your personal data and how we will treat it.
        </p>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          IMPORTANT INFORMATION AND WHO WE ARE
        </h2>
        <p className="mb-5 text-base md:text-lg">
          Cayson Global Ltd is the data controller and is responsible for your
          personal data (collectively referred to as{" "}
          <span className="font-bold">
            “Cayson Global”, “we”, “us” or “our”
          </span>{" "}
          in this Privacy Policy). This means that we are responsible for
          deciding how we hold and use personal data about you.
        </p>
        <p className="mb-5 text-base md:text-lg">
          If you have any questions about this Privacy Policy, please contact us
          using the details set out below:
        </p>
        <div className="bg-gray-200 p-2">
          <p className="mb-5 text-base md:text-lg font-bold">
            Cayson Global Ltd
          </p>
          <p className="mb-5 text-base md:text-lg">
            <span className="font-bold">Address:</span> C/O Mercury Hamlin &
            Associates FZ-LLC, Office 904, JBC 4, Cluster N, Jumeriah Lake
            Towers, PO Box No. 336618, UAE
          </p>
          <p className="mb-5 text-base md:text-lg">
            <span className="font-bold">Email Address:</span>{" "}
            <a
              href="mailto:support@zorqk.com"
              className="text-blue-500 underline"
            >
              support@zorqk.com
            </a>
          </p>
        </div>
        <p className="mb-5 text-base md:text-lg mt-5">
          You have the right to make a complaint at any time to the Information
          Commissioner’s Office (“ICO”) the UK supervisory authority for data
          protection issues{" "}
          <a
            href="https://www.ico.org.uk"
            target="_blank"
            rel="noreferrer"
            className="text-blue-500"
          >
            (www.ico.org.uk)
          </a>
          or country-specific equivalent body. We would, however, appreciate the
          chance to deal with your concerns before you approach the ICO so
          please contact us in the first instance.
        </p>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          CHANGES TO THE PRIVACY POLICY AND YOUR DUTY TO INFORM US OF CHANGES
        </h2>
        <p className="mb-5 text-base md:text-lg">
          We keep our Privacy Policy under regular review.
        </p>
        <p className="mb-5 text-base md:text-lg">
          This version was last updated in July 2025. It may change and, if it
          does, those changes will be posted on this page and notified to you by
          push notification when you next start the App.
        </p>
        <p className="mb-5 text-base md:text-lg">
          You may from time to time be required to read and acknowledge the
          changes to continue your use of the App or the Services.
        </p>
        <p className="mb-5 text-base md:text-lg">
          It is important that the personal data we hold about you is accurate
          and current. Please keep us informed if your personal data changes
          during our relationship with you.
        </p>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          THIRD PARTY LINKS AND SITES
        </h2>
        <p className="mb-5 text-base md:text-lg">
          Our App and Services may, from time to time, contain links to and from
          the websites of third parties, such{" "}
          <a
            href="https://www.autotrader.co.uk/?msockid=30abed716a4c616f21ecf8a76b6b6044"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline"
          >
            AutoTrader
          </a>{" "}
          and{" "}
          <a
            href="https://percyfinance.co.uk/"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline"
          >
            Percy Finance
          </a>{" "}
          (“<span className="font-bold">Third Parties</span>”). Please note that
          these websites (and any services accessible through them) are
          controlled by those Third Parties and are not covered by this Privacy
          Policy.
        </p>
        <p className="mb-5 text-base md:text-lg">
          You should review the relevant Third Party privacy notices to
          understand how the relevant Third Party uses your personal data before
          you submit any personal data to these websites or use these services.
        </p>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          THE DATA WE COLLECT ABOUT YOU
        </h2>
        <p className="mb-5 text-base md:text-lg">
          We collect, use, store and transfer different kinds of personal data
          about you. To make it easier for you to use this Privacy Policy, we
          group these into the following categories. Each of these categories is
          described in more detail below.
        </p>
        <table className="mb-8 text-base md:text-lg w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border px-2 py-1 text-left w-60">
                Type of Personal Data
              </th>
              <th className="border px-2 py-1 text-left">Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border px-2 py-1">Identity Data</td>
              <td className="border px-2 py-1">
                first name, last name, title, date of birth and Profile Data.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Contact Data</td>
              <td className="border px-2 py-1">
                first name, last name, contact address, email address and
                telephone numbers, your communication preferences and copies of
                the communications between you and us
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Profile Data</td>
              <td className="border px-2 py-1">
                your email address, username and password.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Transaction Data</td>
              <td className="border px-2 py-1">
                billing and delivery addresses, payment card details, history of
                your payments, purchases, deliveries, returns and refunds and
                the applicable terms and conditions of your purchases.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Device Data</td>
              <td className="border px-2 py-1">
                the type of device you use, your unique device identifier,
                mobile network information, your mobile operating system, the
                type of mobile browser you use, IP address, and time zone
                setting.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Content Data</td>
              <td className="border px-2 py-1">
                information that you store or generate in the App and the
                Services, being photos, posts and messages.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Usage Data</td>
              <td className="border px-2 py-1">
                logs and detail of your use of our Apps and Services, being the
                dates and times on which you download, access and update the App
                and our Services, any error or debugging information, and the
                resources that you access and the actions we and you take in
                relation to them and Cookies Data.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Security Data</td>
              <td className="border px-2 py-1">
                information we collect about your use of the App and our
                Services in order to ensure your and our other users' safety and
                security, being Usage Data, the Cookies Data generated, and the
                information provided to us by our payment processing provider.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Cookies Data</td>
              <td className="border px-2 py-1">
                the information collected through the cookies and similar
                technologies listed in our Cookies Notice available at:{" "}
                <span className="underline text-blue-500">
                  https://zorqk.com/policies/cookie-policy
                </span>
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Direct Marketing Data</td>
              <td className="border px-2 py-1">
                your direct marketing preferences, consents for receiving direct
                marketing from us and/or our third parties and the history of
                the direct marketing communications we have sent to you.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Connected Data</td>
              <td className="border px-2 py-1">
                information stored on your Device that you permit the App to
                connect to, being Contacts lists and login information.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Feedback Data</td>
              <td className="border px-2 py-1">
                your feedback and survey responses.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Personalisation Data</td>
              <td className="border px-2 py-1">
                Cookies Data, Device Data, Content Data, Transaction Data,
                Connected Data, Usage Data and the preferences we have inferred
                you have and use to personalise the App and Services.
              </td>
            </tr>
          </tbody>
        </table>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          SPECIAL CATEGORIES OF PERSONAL DATA
        </h2>
        <p className="mb-5 text-base md:text-lg">
          We will not process any special categories of personal data about you.
        </p>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          CRIMINAL OFFENCE DATA
        </h2>
        <p className="mb-5 text-base md:text-lg">
          We do not intentionally collect criminal offence data about you.
          However, We may process data relating to criminal offences in
          monitoring the use of our App for security purposes, where we suspect
          you may have committed a crime, such as attempting to make a
          fraudulent purchase or claim or circumvent the security of the App or
          Services.
        </p>
        <p className="mb-5 text-base md:text-lg">
          In such circumstances we will provide that information to law
          enforcement and/or use it to establish, exercise or defend a legal
          claim. In those circumstances, according to the type of activity and
          purpose, we will rely on legitimate interests (protecting our
          business, employees and other users) and legal obligation (where
          required by legal, judicial or law enforcement to disclose or process
          that information).
        </p>
        <p className="mb-5 text-base md:text-lg">
          UK law authorises that processing under the Data Protection Act 2018
          and although the appropriate authorisation will depend on a
          case-by-case basis, monitoring for criminal behaviour through the use
          of our App is in the Substantial public interest (preventing or
          detecting unlawful acts) and processing information related to
          suspected criminal activity for legal claims is permitted under the
          additional condition of legal claims authorised under applicable
          national law.
        </p>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          HOW IS YOUR PERSONAL DATA COLLECTED?
        </h2>
        <p className="mb-5 text-base md:text-lg">
          We collect your personal data in the following way:
        </p>
        <table className="mb-8 text-base md:text-lg w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border px-2 py-1 text-left">
                Method of collection
              </th>
              <th className="border px-2 py-1 text-left">Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border px-2 py-1">Registration.</td>
              <td className="border px-2 py-1">
                We collect your Identity Data and Contact Data when you register
                your interest in relation to any vehicles you wish to purchase.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Communications.</td>
              <td className="border px-2 py-1">
                When you communicate with us via email, telephone, one of our
                online forms or chat we collect your Contact Data.
                <br />
                <br />
                If you contact us through the App using the chat function, we
                also collect Device and Cookies Data to operate our chat
                function and respond to your messages. If the communication
                relates to an error or problem you are having with the App or
                one of our Services, we will also collect Usage Data for
                diagnosis and improvement.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                Information you generate when using the App and Services.
              </td>
              <td className="border px-2 py-1">
                Each time you access and use our App and Services we collect
                Content Data, Device Data, Cookies Data, Personalisation Data
                and Usage Data.
                <br />
                <br />
                We collect Content Data where you upload it to the App or
                interact with the content available on the App. We collect
                Device Data, Cookies Data, Personalisation Data and Usage Data
                by using cookies and other similar technologies.
                <br />
                <br />
                Please see our Cookies Policy available at:{" "}
                <span className="underline text-blue-500">
                  https://zorqk.com/policies/cookie-policy
                </span>
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                Information we collect through monitoring the use of the App,
                our website and Services.
              </td>
              <td className="border px-2 py-1">
                Each time you access and use the App and Services we collect
                information about that access and use, being Device Data,
                Content Data, Cookies Data, and Usage Data.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Direct Marketing.</td>
              <td className="border px-2 py-1">
                We collect and record Direct Marketing Data when we add you to
                our marketing database, you request to change your direct
                marketing preferences, or you interact with our direct marketing
                communications, or we receive prospect information from our data
                broker partners.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Connected Data.</td>
              <td className="border px-2 py-1">
                We collect Connected Data when you choose to connect your
                connected device to your Account.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                Information we receive from third parties and publicly available
                sources.
              </td>
              <td className="border px-2 py-1">
                We will receive personal data about you from the third parties
                and public sources set out below:
                <ul className="list-disc pl-5 mt-2">
                  <li>
                    Device Data and Cookies Data from the following parties:
                  </li>
                  <ul className="pl-5 mt-1" style={{ listStyleType: "circle" }}>
                    <li>
                      analytics providers based inside and/or outside the UK;
                    </li>
                    <li>
                      advertising networks based inside and/or outside the UK;
                    </li>
                    <li>
                      search information providers based inside and/or outside
                      the UK;
                    </li>
                  </ul>
                  <li>
                    Contact Data, Financial Data and Transaction Data from
                    providers of technical, payment and delivery services based
                    in inside and/or outside the UK;
                  </li>
                  <li>
                    Identity Data, Contact Data and Direct Marketing Data from
                    data brokers or aggregators based inside and/or outside the
                    UK;
                  </li>
                  <li>
                    Identity Data and Contact Data from publicly available
                    sources such as Companies House and the electoral register
                    based inside the UK.
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Unique application numbers.</td>
              <td className="border px-2 py-1">
                When you want to install or uninstall the App containing a
                unique application number or when such a Service searches for
                automatic updates, that number and information about your
                installation, for example, the type of operating system, may be
                sent to us.
              </td>
            </tr>
          </tbody>
        </table>
        <h2 className="font-bold text-lg md:text-2xl my-8">Cookies</h2>
        <p className="mb-5 text-base md:text-lg">
          We use cookies (small files placed on your device) and other tracking
          technologies on the App and in our direct marketing emails to improve
          your experience and our development of the App and our Services.
        </p>
        <p className="mb-5 text-base md:text-lg">
          For detailed information on the cookies we use, the purposes for which
          we use them and how you can exercise your choices regarding our use of
          your cookies, see our Cookies Policy available at:{" "}
          <span className="underline text-blue-500">
            https://zorqk.com/policies/cookie-policy
          </span>
        </p>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          HOW WE USE YOUR PERSONAL DATA
        </h2>
        <p className="mb-5 text-base md:text-lg">
          We will only use your personal data when we have a lawful basis to do
          so. Our lawful basis for each purpose for which we use your personal
          data is specified below. Most commonly we will use your personal data
          in the following circumstances:
        </p>
        <ul className="list-disc pl-5 space-y-2 mb-8 text-base md:text-lg">
          <li>
            <strong>Consent.</strong> Where you have freely consented before the
            processing in a specific, informed and unambiguous indication of
            what you want. You can withdraw your consent at any time by
            accessing the [Insert] section of your Account or by contacting us
            at:{" "}
            <a
              href="mailto:support@zorqk.com"
              className="text-blue-500 underline"
            >
              support@zorqk.com
            </a>
          </li>
          <li>
            <strong>Performance of a contract.</strong> Where we need to process
            your personal data to perform a contract with you or where you ask
            us to take steps before we enter into a contract with you. Where we
            rely on performance of a contract and you do not provide the
            necessary information, we will be unable to perform your contract.
          </li>
          <li>
            <strong>Legitimate interests.</strong> Where it is necessary for our
            legitimate interests (or those of a third party) and your interests
            and fundamental rights do not override those interests. We make sure
            we consider and balance any potential impact on you (both positive
            and negative) and your rights before we process your personal data
            for our legitimate interests. You can obtain further information
            about how we assess our legitimate interests against any potential
            impact on you in respect of specific activities by contacting us.
          </li>
          <li>
            <strong>Legal obligation.</strong> Where we need to use your
            personal data to comply with a legal or regulatory obligation. Where
            we rely on legal obligation and you do not provide the necessary
            information, we may be unable to fulfil a right you have or comply
            with our obligations to you, or we may need to take additional
            steps, such as informing law enforcement or a public authority or
            applying for a court order.
          </li>
        </ul>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          EXAMPLES OF HOW WE USE YOUR PERSONAL DATA
        </h2>
        <table className="mb-8 text-base md:text-lg w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border px-2 py-1 text-left">
                Purpose or activity
              </th>
              <th className="border px-2 py-1 text-left">
                Type of personal data
              </th>
              <th className="border px-2 py-1 text-left">
                Lawful basis for processing
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={3}>
                <strong>
                  DELIVERY AND IMPROVEMENT OF OUR APP AND SERVICES
                </strong>
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To permit you to install the App and register you as a new App
                user.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Identity Data</li>
                  <li>Contact Data</li>
                  <li>Financial Data</li>
                  <li>Device Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Performance of a contract.
                <br />
                Legitimate interests (delivering our App to you).
              </td>
            </tr>

            <tr>
              <td className="border px-2 py-1">
                To take steps towards providing you with services at your
                request, to process and fulfil in-App orders/purchases and
                deliver services to you, including managing payments and sending
                you service communications.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Identity Data</li>
                  <li>Contact Data</li>
                  <li>Transaction Data</li>
                  <li>Device Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">Performance of a contract.</td>
            </tr>

            <tr>
              <td className="border px-2 py-1">
                Enforce our terms and conditions, including to collect money
                owed to us.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Identity Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (to recover debts due to us).
              </td>
            </tr>

            <tr>
              <td colSpan={3}>
                <strong>DIRECT MARKETING</strong>
              </td>
            </tr>

            <tr>
              <td className="border px-2 py-1">
                To send you direct marketing communications via email, text
                and/or push notification.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                  <li>Device Data</li>
                  <li> Direct Marketing</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Consent. Unless we can rely on the soft opt-in and you have not
                opted out, in which case we rely on Legitimate Interest (to
                publicise and grow our business).
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To send you direct marketing communications by telephone or
                post.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                  <li>Device Data</li>
                  <li> Direct Marketing Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (to publicise and grow our business).
                <br />
                Unless you have opted out, in which case we rely on Consent.
              </td>
            </tr>

            <tr>
              <td colSpan={3}>
                <strong>TROUBLESHOOTING, IMPROVEMENT AND SECURITY</strong>
              </td>
            </tr>

            <tr>
              <td className="border px-2 py-1">
                To administer, monitor and improve our business, Services and
                this App including troubleshooting, data analysis and system
                testing.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Identity Data</li>
                  <li>Contact Data</li>
                  <li> Device Data </li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (for running our business, provision of
                administration and IT services, network security, maintaining
                the security of our App and Services, providing a secure service
                to users and preventing fraudulent and other misuse of our App).
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                Applying security measures to our processing of your personal
                data, including processing in connection with the App.
              </td>
              <td className="border px-2 py-1">
                All personal data under this Privacy Policy
              </td>
              <td className="border px-2 py-1">
                Legal obligation (applying appropriate technical and
                organisational measures under Article 32 of the UK GDPR).
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                Otherwise monitoring use of the App and deploying appropriate
                security measures.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                  <li>Security Data</li>
                  <li>Transaction Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (running our business, provision of
                administration and IT services, network security, maintaining
                the security of our App and services, providing a secure service
                to users and preventing fraudulent and other misuse of our App).
              </td>
            </tr>

            <tr>
              <td colSpan={3}>
                <strong>RIGHTS AND OBLIGATIONS</strong>
              </td>
            </tr>

            <tr>
              <td className="border px-2 py-1">
                To comply with our other legal obligations, including compliance
                with tax legislation, judicial, law enforcement and government
                authorities' requests.
              </td>
              <td className="border px-2 py-1">
                All personal data under this Privacy Policy
              </td>
              <td className="border px-2 py-1">Legal obligation.</td>
            </tr>

            <tr>
              <td colSpan={3}>
                <strong>COOKIES AND PERSONALISATION</strong>
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To deploy and process personal data collected via Cookies that
                are strictly necessary, as set out in the Cookies Policy
                available at:{" "}
                <span className="underline text-blue-500">
                  {" "}
                  https://zorqk.com/policies/cookie-policy
                </span>
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Cookies Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (delivering and securing the App and our
                Services).
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To deploy and process personal data collected via Cookies that
                are not strictly necessary, as set out in the Cookies Policy
                available at:{" "}
                <span className="underline text-blue-500">
                  {" "}
                  https://zorqk.com/policies/cookie-policy
                </span>
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Cookies Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">Consent.</td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To deliver (personalised) advertisements to you.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Personalisation</li>
                </ul>
              </td>
              <td className="border px-2 py-1">Consent.</td>
            </tr>

            <tr>
              <td colSpan={3}>
                <strong>OTHER COMMUNICATIONS</strong>
              </td>
            </tr>

            <tr>
              <td className="border px-2 py-1">
                To notify you of changes to the App, Services, your purchases
                and our terms and conditions for ongoing contracts.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                For ongoing or prospective contracts, Performance of a contract.
                Otherwise, Legitimate interests (in servicing our users and
                prospective users).
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To notify you of updates to this Privacy Policy.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                  <li>Transaction Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legal obligation (to inform you of our processing under Articles
                13 and 14 of the UK GDPR.)
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To respond to your requests to exercise your rights under this
                notice
              </td>
              <td className="border px-2 py-1">As relevant to your request.</td>
              <td className="border px-2 py-1">
                Legal obligation (complying with data subject requests under
                Chapter 3 of the UK GDPR.)
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To enable you to participate in a prize draw or competition
                (where applicable, please also see the separate prize or
                competition privacy notice)
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                  <li>Direct Marketing Data</li>
                  <li>Competition Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legal obligation (complying with data subject requests under
                Chapter 3 of the UK GDPR.)
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To ask you to complete a survey and process your response (where
                applicable, please also see the separate Privacy Policy).
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (to analyse how users use our products or
                Services and to develop them and grow our business).Unless you
                have previously opted out, where we will rely on Consent.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                To otherwise respond to your enquiries, fulfil your requests and
                to contact you where necessary.
              </td>
              <td className="border px-2 py-1">
                As relevant to your enquiry or request
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (service our users and prospective users).
              </td>
            </tr>

            <tr>
              <td colSpan={3}>
                <strong>PERSONAL DATA SHARING</strong>
              </td>
            </tr>

            <tr>
              <td className="border px-2 py-1">
                Sell personal data with our third- party providers for the
                purpose(s) of data analytics and direct marketing purposes.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                  <li>Direct Marketing Data</li>
                  <li>Competition Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">Consent.</td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                Share personal data with our third-party providers for purposes
                not otherwise set out above.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                  <li>Direct Marketing Data</li>
                  <li>Competition Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (for the purpose relevant to the recipient,
                as set out at "Disclosures of your personal data”)
              </td>
            </tr>

            <tr>
              <td colSpan={3}>
                <strong>BUSINESS CONTACTS</strong>
              </td>
            </tr>

            <tr>
              <td className="border px-2 py-1">
                Process personal data relating to staff members of our business
                contacts, including suppliers, customers and prospects.
              </td>
              <td className="border px-2 py-1">
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>Contact Data</li>
                </ul>
              </td>
              <td className="border px-2 py-1">
                Legitimate interests (servicing and receiving products or
                services, to or from our business contacts and carry out our B2B
                business).
              </td>
            </tr>
          </tbody>
        </table>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          AUTOMATED DECISION MAKING AND PROFILING
        </h2>
        <p className="mb-5 text-base md:text-lg">
          We do not make decisions based solely on automated processing or
          profiling that produce legal effects concerning you (or have similarly
          significant effects).
        </p>
        <h2 className="font-bold text-lg md:text-2xl my-8">
          DISCLOSURES OF YOUR PERSONAL DATA
        </h2>
        <p className="mb-5 text-base md:text-lg">
          We may share your personal data with the following third parties:
        </p>
        <ul
          className="pl-5 space-y-2 mb-5 text-base md:text-lg"
          style={{ listStyleType: "square" }}
        >
          <li>
            <strong>External third parties.</strong>
            {"  "} This includes:
            <ul className="pl-5 mt-2" style={{ listStyleType: "circle" }}>
              <li>
                Your AppStore Provider and mobile network operator to allow you
                to install the App.
              </li>
              <li>
                Service providers acting as processors based in the United
                Kingdom and provide IT and system administration services,
                hosting services for our App, delivery and logistics services,
                payment processing, fraud and identity verification providers,
                customer service support, email delivery and administration, and
                data storage and analysis.
              </li>
              <li>
                Our professional advisors acting as controllers based in the UK
                including lawyers, auditors, insurers, consultants.
              </li>
              <li>
                Your service providers that you have appointed and we need to
                contact to fulfil your requests such as your banking or payment
                card provider to process your transactions.
              </li>
              <li>
                Marketing and promotional partners and co-operatives acting as
                processors or controllers or joint controllers based inside OR
                outside of the UK with whom we share data to enhance our
                offerings and identify prospective customers.
              </li>
              <li>
                Third party partners where you have expressly subscribed to
                receive marketing from or with them.
              </li>
            </ul>
          </li>
          <li>
            Third parties to whom we may choose to sell, transfer or merge parts
            of our business or our assets. Alternatively, we may seek to acquire
            other businesses or merge with them. If a change happens to our
            business, then the new owners may use your personal data in the same
            way as set out in this Privacy Policy.
          </li>
          <li>
            HM Revenue and Customs, regulators, law enforcement, public
            authorities or other third parties acting as controllers based in
            the UK where necessary to exercise our rights or comply with a legal
            obligation
          </li>
        </ul>

        <p className="mb-5 text-base md:text-lg">
          We may also from time to time sell your personal data to the following
          third parties:
        </p>
        <ul
          className="pl-5 space-y-2 mb-5 text-base md:text-lg"
          style={{ listStyleType: "square" }}
        >
          <li>
            <strong>External third parties.</strong> This includes:
          </li>
          <ul className="pl-5 mt-2" style={{ listStyleType: "circle" }}>
            <li>
              Big Geo Inc based in Canada and the United States and Amazon Web
              Services Inc based in the United States for the purpose(s) of:
              date analytics and direct marketing purposes provided that we have
              obtained your prior consent before doing so. You will be able to
              withdraw your consent at any time (see YOUR LEGAL RIGHTS below for
              more information).
            </li>
          </ul>
        </ul>

        <h2 className="font-bold text-lg md:text-2xl my-8">
          INTERNATIONAL TRANSFERS
        </h2>
        <p className="mb-5 text-base md:text-lg">
          Whenever we transfer (including where we sell) your personal data out
          of the UK, we ensure a similar degree of protection is afforded to it
          by ensuring the following safeguard is implemented:
        </p>

        <ul
          className="pl-5 space-y-2 mb-5 text-base md:text-lg"
          style={{ listStyleType: "square" }}
        >
          <li>
            We use specific standard contractual terms approved for use in the
            UK which give the transferred personal data the same protection as
            it has in the UK, namely the International Data Transfer Agreement.
            To obtain a copy of these contractual safeguards, please contact us
            at{" "}
            <a
              href="mailto:support@zorqk.com"
              className="text-blue-500 underline"
            >
              support@zorqk.com
            </a>
          </li>
        </ul>

        <p className="mb-5 text-base md:text-lg">
          We may also transfer your personal data to service providers that
          carry out certain functions on our behalf. This may involve
          transferring personal data outside the UK to countries which have laws
          that do not provide the same level of data protection as the UK law.
        </p>

        <p className="mb-5 text-base md:text-lg">
          Whenever we transfer your personal data out of the UK to service
          providers, we ensure a similar degree of protection is afforded to it
          by ensuring that the following safeguard is in place:
        </p>

        <ul
          className="pl-5 space-y-2 mb-5 text-base md:text-lg"
          style={{ listStyleType: "square" }}
        >
          <li>
            We use specific standard contractual terms approved for use in the
            UK which give the transferred personal data the same protection as
            it has in the UK, namely the International Data Transfer Agreement.
            To obtain a copy of these contractual safeguards, please contact us
            at{" "}
            <a
              href="mailto:support@zorqk.com"
              className="text-blue-500 underline"
            >
              {" "}
              support@zorqk.com
            </a>
          </li>
        </ul>

        <p className="mb-5 text-base md:text-lg">
          Please contact{" "}
          <a
            href="mailto:support@zorqk.com"
            className="text-blue-500 underline"
          >
            support@zorqk.com
          </a>{" "}
          using the contact details above if you want further information on the
          specific mechanism used by us when transferring your personal data out
          of the UK.
        </p>

        <h2 className="font-bold text-lg md:text-2xl my-8">DATA SECURITY</h2>
        <p className="mb-5 text-base md:text-lg">
          All information you provide to us is stored on our secure servers and
          located in the UK.
        </p>

        <p className="mb-5 text-base md:text-lg">
          Any payment transactions carried out by us, or our chosen third-party
          provider of payment processing services will be encrypted using
          Secured Sockets Layer <strong>(“SSL”)</strong> technology.
        </p>

        <p className="mb-5 text-base md:text-lg">
          Where we have given you (or where you have chosen) a password that
          enables you to access certain parts of our App or Services, you are
          responsible for keeping this password confidential. We ask you not to
          share a password with anyone.
        </p>

        <p className="mb-5 text-base md:text-lg">
          Once we have received your information, we will use strict procedures
          and security features to protect your personal data from loss,
          unauthorised use or access.
        </p>

        <p className="mb-5 text-base md:text-lg">
          We will collect and store personal data on your device using
          application data caches and browser web storage (including HTML5) and
          other technology. Please see our Cookies Policy available at:{" "}
          <span className="underline text-blue-500">
            https://zorqk.com/policies/cookie-policy
          </span>
        </p>

        <p className="mb-5 text-base md:text-lg">
          Certain Services may include social networking, chat room or forum
          features. Ensure when using these features that you do not submit any
          personal data that you do not want to be seen, collected or used by
          other users.
        </p>
        <p className="mb-5 text-base md:text-lg">
          We have put in place procedures to detect and respond to personal data
          breaches and notify you and any applicable regulator when we are
          legally required to do so.
        </p>

        <h2 className="font-bold text-lg md:text-2xl my-8">DATA RETENTION</h2>
        <p className="mb-5 text-base md:text-lg">
          By law we have to keep basic information about our customers
          (including Contact Data, Identity Data, Security Data and Transaction
          Data) for six (6) years after they cease being customers for tax
          purposes.
        </p>

        <p className="mb-5 text-base md:text-lg">
          In some circumstances you can ask us to delete your data.
        </p>
        <p className="mb-5 text-base md:text-lg">
          Once we no longer have a legal right to hold your personal data, we
          will delete or, in some circumstances, we will anonymise your personal
          data (so that it can no longer be associated with you) for research or
          statistical purposes, in which case we may use, share and/or sell this
          information indefinitely without further notice to you.
        </p>
        <p className="mb-5 text-base md:text-lg">
          If you do not send us any expressions of interest for any vehicles
          after 2 (two) years then we will delete your personal data.
        </p>

        <h2 className="font-bold text-lg md:text-2xl my-8">
          YOUR LEGAL RIGHTS
        </h2>
        <p className="mb-5 text-base md:text-lg">
          You have the following rights under data protection laws in relation
          to your personal data.
        </p>
        <table className="mb-8 text-base md:text-lg w-full border-collapse">
          <thead>
            <tr>
              <th className="border px-2 py-1 text-left w-60">
                Right in relation to your personal data
              </th>
              <th className="border px-2 py-1 text-left">Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border px-2 py-1">Access</td>
              <td className="border px-2 py-1">
                Request access to and/or a copy of the personal data we process
                about you (commonly known as a data subject access request).
                This enables you to check that we are lawfully processing it.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Correction</td>
              <td className="border px-2 py-1">
                Request correction of any incomplete or inaccurate data we hold
                about you. (We may need to verify the accuracy of the new data
                you provide to us).
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Deletion</td>
              <td className="border px-2 py-1">
                Request us to delete or remove personal data where there is no
                good reason for us continuing to process it. You also can ask us
                to delete or remove your personal data where you have
                successfully exercised your right to object to processing, where
                we have processed your information unlawfully or where we need
                to erase your personal data to comply with law. In some cases,
                we may need to continue to retain some of your personal data
                where required by law. If these apply, we will notify you at the
                time of our response.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Objection</td>
              <td className="border px-2 py-1">
                Object to us processing your personal data where:
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>
                    we are relying on legitimate interests as the lawful basis
                    and you feel the processing impacts on your fundamental
                    rights and freedoms, or
                  </li>
                  <li>the processing is for direct marketing purposes</li>
                </ul>
                <br />
                In some cases, we may refuse your objection if we can
                demonstrate that we have compelling legitimate grounds to
                continue processing your information which override your rights
                and freedoms.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Restriction</td>
              <td className="border px-2 py-1">
                Request that we restrict or suspend our processing of your
                personal data:
                <ul className="pl-5 mt-2" style={{ listStyleType: "square" }}>
                  <li>if you want us to establish the data's accuracy;</li>
                  <li>
                    where our use of the data is unlawful, but you do not want
                    us to erase it;
                  </li>
                  <li>
                    where we no longer require it, but you need us to hold onto
                    it to establish, exercise or defend legal claims; or
                  </li>
                  <li>
                    you have objected to our use of your data, but we need to
                    verify whether we have overriding legitimate grounds to use
                    it.
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Data portability</td>
              <td className="border px-2 py-1">
                Request we transfer certain of your personal data to you or your
                chosen third party in a structured, commonly used,
                machine-readable format. This right only applies to information
                processed by automated means that we process on the lawful bases
                of consent or performance of a contract.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">Withdraw consent</td>
              <td className="border px-2 py-1">
                Withdraw your consent at any time where we are relying on
                consent to process your personal data.
                <br />
                <br />
                Please know that this does not affect the lawfulness of any
                processing carried out before you withdraw your consent, and
                after withdrawal, we may not be able to provide certain products
                or services to you. We will advise you if this is the case at
                the time you withdraw your consent.
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">
                Complain to the UK data protection regulator
              </td>
              <td className="border px-2 py-1">
                As mentioned above, you have the right to make a complaint at
                any time to the Information Commissioner’s Office (“ICO”) the UK
                supervisory authority for data protection issues
                <a
                  href="https://www.ico.org.uk"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500"
                >
                  (www.ico.org.uk)
                </a>{" "}
                or country-specific equivalent body.
                <br />
                We would, however, appreciate the chance to deal with your
                concerns before you approach the ICO so please contact us in the
                first instance.
              </td>
            </tr>
          </tbody>
        </table>

        <p className="mb-5 text-base md:text-lg">
          You can exercise any of these rights at any time by contacting us at:{" "}
          <a
            href="mailto:support@zorqk.com"
            className="text-blue-500 underline"
          >
            support@zorqk.com
          </a>
        </p>
      </div>
    </section>
  );
};

export default PrivacyPolicy;

// import React from "react";
// // import Accordion from "../../components/ui/Accordion";

// const PrivacyPolicy = () => {
//   //   const faqItems = [
//   //     {
//   //       question: "Use the ZORQK app",
//   //       answer: `If you choose to download and install the ZORQK app, you’ll be able to create an account, browse and purchase a car, and apply for finance in the same way as if you visit the ZORQK website.  We’ll collect and use your personal data for the same purposes explained in this Privacy Notice. In addition, we'll provide useful information through the app, such as the MOT due date for your car and a summary of your finance agreement and term remaining, if you have taken out finance through ZORQK.

//   //         Device level permissions are needed when the app is installed, in order to access file storage to upload documents for finance applications.

//   //         We also use cookies and similar technologies in our app. Some are necessary and cannot be switched off. Other cookies are optional for marketing purposes. You can control the use of these at any time by visiting Settings in the app. More information on our use of cookies is available in our Cookie Policy.`,
//   //     },
//   //     {
//   //       question: "Creating and managing your ZORQK account",
//   //       answer: `To create an account we’ll need:

//   // - your name;

//   // - e-mail address; and

//   // - a password.

//   // Other than that, you aren’t obliged to provide any more personal data, but in addition to the above, you are able to enter the following:

//   // - Your phone number; and

//   // - Your marketing preferences

//   // You can also save favourites to your account. As part of this function, we will keep you informed if the car's price changes or if the car is no longer available.

//   // As explained in our Cookie Policy, if you allow the use of cookies we’ll also use this data to create a personalised ZORQK profile to tailor our content, both on our website and in our marketing communications.

//   // Lawful basis: performance of a contract (namely, our Terms of Use) and our legitimate interests in creating a personalised ZORQK profile for you, to allow you to manage your account, store your favourite search results and view your order history, and for us to communicate with you about your favourited cars.`,
//   //     },
//   //     {
//   //       question: "Using your personal information for marketing purposes",
//   //       answer: `We may use your personal data to send you marketing by post, by phone, by email and by SMS message. You will always be in full control of any marketing we provide to you.

//   // Lawful basis: We will only use your personal data for marketing purposes if we have your consent or a legitimate interest, depending on how and when we have collected your details (for example via a third party such as our event sponsorship partners, or when you create an account with ZORQK).

//   // Where we provide newsletters, marketing communications or other offers or information, we’ll use your data to tailor and send these to you, until you change or cancel your marketing preferences.

//   // Lawful basis: where we have a lawful basis to provide you with newsletters,  marketing communications or other offers or information from us,  we rely on our legitimate interests where we tailor those communications to suit your preferences – we do this so that we can provide you with communications and offers that are relevant to you.`,
//   //     },
//   //     {
//   //       question: "Creating a more personalised experience",
//   //       answer: `We’ll analyse captured data and share certain personal data with third parties to expand our knowledge of customer categories, use and habits though matching the personal data we collect with other databases. Allowing us to create a more personalised experience for different customer segments and improve ZORQK.

//   // Lawful basis: where we have a lawful basis to provide you with marketing, we rely on our legitimate interests to share the data we collect such as name, postcode, address and telephone number with credit agencies and customer matching databases. This enables us to understand our customers better and improve ZORQK in line with customer expectations.`,
//   //     },
//   //     {
//   //       question: "Managing your marketing preferences",
//   //       answer: `If you receive any marketing communications from ZORQK and don’t wish to receive such information in future, then please log in to your account to change your preferences, call us or email us at hello@ZORQK.co.uk. You will also have the opportunity to opt out of future communications whenever ZORQK sends you any such material, using the instructions provided.`,
//   //     },
//   //     {
//   //       question: "Selling a car to you",
//   //       answer: `When you find a car that you would like to buy from ZORQK, we’ll need the following information from you to enter into the contract of sale with you, process the payment and arrange delivery or collection (as applicable) and to pick up your part exchange car if applicable.:

//   // - Name

//   // - Mobile phone number

//   // - E-mail address

//   // - Current address (we’ll ask for your proof of address upon delivery or collection too by asking for a photocard UK driving licence or other form of ID accepted by us)

//   // - Payment details

//   // If the registered keeper of the part exchange car is not you then we will also need this information from the registered keeper and this Privacy Notice will apply to our use of their data as well.

//   // Lawful basis: performance of a contract (namely, our Car Purchase Terms and Conditions).`,
//   //     },
//   //     {
//   //       question: "Helping to check your financial eligibility to purchase a car",
//   //       answer: `If you would like to receive an indication of your financial eligibility, you can choose to carry out a soft credit search (which is arranged via our third party broker, Evolution Funding). We’ll need to collect the following information from you which we will provide to Evolution Funding:

//   // - Full name

//   // - Date of birth

//   // - Current address

//   // - Previous addresses if less than 3 years at current address

//   // Lawful basis: performance of a contract. To provide you with the requested information on your financial eligibility when looking to purchase a car with ZORQK. We collect this information on behalf of our third-party finance broker, Evolution Funding who carries out the eligibility check. You can view the privacy notice or information about how they use your data, including the section on "Conducting Credit Searches"`,
//   //     },
//   //     {
//   //       question: "Helping to arrange financing for your purchase of a car",
//   //       answer: `If you would like to purchase a car using our finance option (which is arranged via our third party finance broker, Evolution Funding), we’ll need to collect the following personal data from you which we will provide to Evolution Funding and, in some cases, Black Horse (a lender):

//   // - Title;

//   // - Full name (including middle names);

//   // - Gender;

//   // - Date of birth;

//   // - Marital status;

//   // - Nationality;

//   // - Previous addresses if less than 3 years at current address;

//   // - Employment type and history;

//   // - Employer address;

//   // - Time with employer;

//   // - Mobile phone number;

//   // - Residential status;

//   // - Driving licence type;

//   // - Outgoings (including monthly mortgage/rent and other general outgoings);

//   // - Gross annual income;

//   // - Type of proof of income;

//   // - Bank account number and sort code; and

//   // - Number of years you have held that bank account

//   // Lawful basis: We collect this personal data on behalf of our third party finance broker, Evolution Funding, and you can view their privacy notice for information about how they use your data independently to ZORQK. Our third party finance broker will also share your personal data with lenders considering your finance application. We may also share your data with Black Horse and you should view their privacy notice for information about how they use your data independently to ZORQK.

//   // Credit Reference Agencies

//   // You should note in particular that our third party finance broker, or the lender that is considering your finance application, will carry out credit and identity checks on you, and will supply your personal data to credit reference agencies (CRAs) who will in turn give them information about you as an output to the credit and identity check.

//   // CRAs will supply to them both public (including the electoral register) and shared credit, financial situation and financial history information and fraud prevention information. When CRAs receive a search from them, the CRA will place a search footprint on your credit file that may be seen by other lenders. Our third party finance broker or lender do this to assess your creditworthiness and product suitability, to check your identity, manage your account, trace and recover debts and prevent criminal activity.

//   // The finance broker or lender will also continue to exchange personal data about you with CRAs on an ongoing basis, including about your settled accounts and any debts not fully repaid on time. CRAs will share your personal data with other organisations. Your personal data will also be linked to the data of your spouse, any joint applicants or other financial associates. The identities of the CRAs, and the ways in which they use and share personal information, are explained in more detail at Equifax, Experian and Transunion (please note that the Credit Reference Agency Information Notice or ‘CRAIN’ is the same for each of the CRAs).

//   // Your lender will tell us your application ID number and will keep us up to date with the progress and outcome of your finance application so that we can enter into the contract of sale with you at the appropriate time.

//   // Verifying your identity

//   // If you choose to purchase a car using our finance option and your finance application is approved by Black Horse then before you can proceed with entering into the finance agreement, we are required to verify your identity on behalf of Black Horse. In order to do this, we will need to collect a “selfie” photograph of you and an image your identity document such as a driving licence. We will use a third party to carry out this identity check who will collect your photograph and document image as well as certain device and location identifiers such as an IP address in order to help correctly identify you and reduce fraud.

//   // Lawful basis: compliance with the legal obligations regarding verification of identity imposed by anti-corruption laws, our legitimate interest in reducing fraud and the performance of a contract (namely your finance agreement with Black Horse).`,
//   //     },
//   //     {
//   //       question: "Contacting you about finance options",
//   //       answer: `During the term of your finance agreement, we may contact you to discuss finance options we believe may be relevant to you, including renewal, voluntary termination, balloon payments, early settlement and the options available at the end of a finance agreement. This is based on personal data we receive from your lender, relating to your outstanding balance or early settlement requests, where there is a lawful basis for them to share this personal data with us.

//   // If you do not wish us to contact you in this way, please see Managing your marketing preferences to unsubscribe.

//   // Lawful basis: Permission to provide marketing materials to you, which may be based on consent or legitimate interests, depending on how and when we have collected your details`,
//   //     },
//   //     {
//   //       question: "Providing customer service and quality control",
//   //       answer: `When we communicate with you about the purchase of your car, or if you contact us with a query or complaint, then we will collect and process your personal data and keep records of our interactions with you (e.g. calls, e-mails, chat sessions, letters), as needed to investigate and provide you an answer or resolution. You may also optionally provide your postcode or location of your nearest town, to enable us to advise on collection or delivery options, and the location of your nearest ZORQK Store. Please note we may record calls and keep records of them and the contents of e-mails.

//   // Lawful basis: performance of a contract (namely, our Terms of Use Car Purchase Terms and Conditions, 90 Day Warranty Terms and Conditions and/or ZORQKCare agreement, as applicable)and our legitimate interests in ensuring that we are able to answer or resolve any queries or complaints properly and efficiently.`,
//   //     },
//   //     {
//   //       question: "Running promotions, contests or prize draws",
//   //       answer: `If you enter a promotion, contest, or prize draw then we will collect and process your personal data in order to manage your entry. We may at the same time give you the opportunity of signing up for an account or opting in to receiving marketing communications but will only use your personal data for that purpose if you choose to do so.

//   // Depending on the nature of the promotion we may ask you for personal data in order for you to make a claim or participate, including your name, address, email address, bank account and sort code and other follow up information in case of any queries.

//   // Lawful basis: performance of a contract, namely our contractual obligations under the terms of the relevant promotion, contest or prize draw – we do this so that we can identify you, to correspond with you regarding the promotion, contest or prize draw, verify your claim is valid and to make any applicable payments or distribute prizes to you (if required).`,
//   //     },
//   //     {
//   //       question: "Market research, surveys and testimonials",
//   //       answer: `We might contact you from time to time to provide feedback on our services.

//   // Lawful basis: our legitimate interest in order to continually improve the products and services that we offer.`,
//   //     },
//   //     {
//   //       question: "Service communications",
//   //       answer: `We may need to contact you in relation to service-related issues, e.g. forgotten account details, technical issues with the service, updates to our terms and conditions, or other non-promotional messages.

//   // Lawful basis: our legitimate interests in ensuring that you can continue to use ZORQK and are aware of our current policies and procedures.`,
//   //     },
//   //     {
//   //       question: "Improving ZORQK",
//   //       answer: `It’s important for us to understand how people use ZORQK so that we can improve and ensure our users receive a good service. We’ll analyse which pages or parts of the website get the most views or attention, and how our users arrive here, so that we can improve. This may include collecting details about your device and network, such as browser version, IP addresses, operating system details, and screen size.

//   // This may also involve the use of cookies or similar technologies as explained in our Cookie Policy

//   // Lawful basis: our legitimate interests in maintaining and optimising our website and improving it where necessary or as identified by trends in the user personal data we analyse, including for security purposes and to protect your information.`,
//   //     },
//   //     {
//   //       question: "Complying with our legal obligations",
//   //       answer: `We’ll use your personal data to assist any public authority or criminal investigation body, for activities relating to the prevention, detection and investigation of crime, for compliance with our legal and regulatory responsibilities, to identify you when you contact us, to comply with a request from you in connection with the exercise of your rights under data protection laws, and to verify the accuracy of personal data we hold about you.

//   // Lawful basis: compliance with a legal obligation.`,
//   //     },
//   //     {
//   //       question: "“Special categories” of data",
//   //       answer: `Under data protection laws, certain “special categories” of personal data are subject to additional protections – this includes data relating to health. We don’t ask you for this type of personal data or intentionally collect it, however if you disclose to us that you have a disability (physical or mental) we might disclose this personal data to our third party finance broker who may tell the relevant lender. This is to make sure we are complying with our legal and regulatory obligations and to evidence that we’ve taken appropriate steps to protect you in the case of a legal claim.

//   // ZORQK does not intend to collect or process any other special categories of personal data e.g. those revealing racial or ethnic origin, political opinions, religious or philosophical beliefs, trade union membership, genetic data or biometric data, or data concerning anyone’s sex life or sexual orientation.`,
//   //     },
//   //   ];

//   //   const faqItems2 = [
//   //     {
//   //       question: "Operating our 90-day warranty",
//   //       answer: `When you purchase a car from ZORQK, a 90-day warranty for mechanical and electrical repairs to the Car is provided at no additional cost to you. This includes breakdown assistance (where the breakdown results from mechanical or electrical defect).

//   // If you make a claim under our 90-day warranty, we’ll share your personal data and that of any other person you provide (as required to process your claim) with our authorised repairers and/or our breakdown agent, as is necessary to provide the benefits of the 90-day warranty to you, which may include:

//   // - Name;

//   // - contact number; and

//   // - car registration number (where this is personal data).

//   // Where it is necessary for you to provide someone else's personal data as part of a claim (for example a family member or other driver of the car) please ensure they are aware their information will be processed in accordance with this Privacy Notice, and have agreed you may share this information on their behalf (before you provide their personal data to us or our partners).

//   // Please see the Data Protection section in the ZORQK 90 Day Warranty Terms and Conditions available here for more information.

//   // Lawful basis: performance of a contract (namely our 90-Day Warranty Terms and Conditions)`,
//   //     },
//   //     {
//   //       question: "Third party products and services",
//   //       answer: `We offer the following third party products and services, available for you to purchase through the ZORQK car purchase journey or your My Account function.

//   // - ZORQKShield (provided by Altro Limited trading as Autoglym)

//   // - ZORQKCharge (provided by Paua Tech Limited)

//   // - ZORQKCover (provided by London General Insurance Company Limited (LGI) and administered by TWG Services Limited (TWG)). LGI and TWG provide their services under the name “Assurant”. LGI is authorised by the Prudential Regulation Authority and regulated by the Financial Conduct Authority and the Prudential Regulation Authority, Financial Services Register No. 202689. TWG is authorised and regulated by the Financial Conduct Authority, Financial Services Register No. 312440.

//   // The following ZORQKCover products and services are available for purchase:

//   // - Extended warranty and breakdown assistance;

//   // - Alloy wheel insurance; and

//   // - Scratch and dent insurance.

//   // How your personal data is used

//   // If you opt to purchase any of the third party products and services described in this section, we may need to share the following personal data with these third parties in order to register your purchase and carry out administration tasks (such as re-directing complaints):

//   // - Title;

//   // - name;

//   // - current address;

//   // - contact phone number and email address;

//   // - car registration number (where this is personal data); and

//   // - customer order ID (where this is personal data).

//   // Lawful basis: performance of a contract (to provide you with ZORQKShield, ZORQKCharge or ZORQKCover at your request).

//   // The third party that provides the product or service will be a controller of any personal data you provide to them. This means that the third party, not ZORQK, decides on the purposes and ways that your personal data is processed (or, in other words, used). ZORQK is not responsible for the actions of these third parties when processing your personal data.

//   // Where these third party products or services apply to you, please review the privacy notice of each controller (as linked below) to ensure you’re comfortable with how that controller will process your personal data independently of ZORQK.

//   // - ZORQKShield - Privacy Policy | Autoglym LifeShine;

//   // - ZORQKCharge - Paua | Privacy (pauatech.com); and

//   // - ZORQKCover - as set out in your policy wording documentation and their privacy policy - Privacy Policy | Assurant.`,
//   //     },
//   //   ];

//   return (
//     <section className="w-full bg-primary text-white relative">
//       <div className="max-w-6xl mx-auto px-4 py-16 pt-32 md:pt-40 flex flex-col md:flex-row items-center">
//         <h1 className="text-brandAccent text-4xl md:text-5xl font-extrabold mb-4">
//           Privacy Policy
//         </h1>
//       </div>
//       <div className="px-4 md:px-12 py-32 rounded-tl-3xl bg-white text-primary">
//         <p className="mb-5 text-base md:text-lg">
//           <span className="font-bold">Effective Date:</span> 07 July 2024{" "}
//           <span className="font-bold pl-3">Last Updated:</span> 07 July 2024
//         </p>

//         <p className="mb-5 text-base md:text-lg">
//           Zorqk.com (“Zorqk”, “we”, “us”, or “our”) is committed to protecting
//           your privacy. This Privacy Policy explains how we collect, use,
//           disclose, and safeguard your personal information when you visit our
//           website and use our services.
//         </p>

//         <h1 className="font-bold text-lg md:text-2xl my-8">
//           1. Information We Collect
//         </h1>
//         <p className="mb-5 text-base md:text-lg">
//           We may collect the following types of information:
//         </p>
//         <ul className="list-disc pl-5 space-y-2 mb-5 text-base md:text-lg">
//           <li>
//             <span className="font-bold">Personal Information:</span> Name, email
//             address, phone number, and other contact details.
//           </li>
//           <li>
//             <span className="font-bold">Account Information:</span> Login
//             credentials, preferences, and saved listings.
//           </li>
//           <li>
//             <span className="font-bold">Vehicle Interest Data:</span> Vehicles
//             viewed, saved, or inquired about.
//           </li>
//           <li>
//             <span className="font-bold">Transaction Data:</span> Purchase
//             history and communication with sellers.
//           </li>
//           <li>
//             <span className="font-bold">Technical Data:</span> IP address,
//             browser type, device information, and cookies.
//           </li>
//         </ul>

//         <h1 className="font-bold text-lg md:text-2xl my-8">
//           2. How We Use Your Information
//         </h1>
//         <p className="mb-5 text-base md:text-lg">We use your information to:</p>
//         <ul className="list-disc pl-5 space-y-2 mb-5 text-base md:text-lg">
//           <li>Provide and improve our services.</li>
//           <li>
//             Facilitate communication between buyers and the listing provider.
//           </li>
//           <li>Respond to inquiries and provide customer support.</li>
//           <li>
//             Send updates, alerts, and promotional content (with your consent).
//           </li>
//           <li>Comply with legal obligations.</li>
//         </ul>

//         <h1 className="font-bold text-lg md:text-2xl my-8">
//           3. Sharing Your Information
//         </h1>
//         <p className="mb-5 text-base md:text-lg">
//           We may share your information with:
//         </p>
//         <ul className="list-disc pl-5 space-y-2 mb-5 text-base md:text-lg">
//           <li>
//             <span className="font-bold">Our Listing Provider:</span> To manage
//             vehicle listings and respond to your inquiries.
//           </li>
//           <li>
//             <span className="font-bold">Service Providers:</span> For hosting,
//             analytics, and customer support.
//           </li>
//           <li>
//             <span className="font-bold">Legal Authorities:</span> When required
//             by law or to protect our rights.
//           </li>
//         </ul>
//         <p className="mb-5 text-base md:text-lg">
//           We do <span className="font-bold">not</span> sell your personal data
//           to third parties.
//         </p>

//         <h1 className="font-bold text-lg md:text-2xl my-8">
//           4. Cookies and Tracking
//         </h1>
//         <p className="mb-5 text-base md:text-lg">
//           We use cookies and similar technologies to:{" "}
//         </p>
//         <ul className="list-disc pl-5 space-y-2 mb-5 text-base md:text-lg">
//           <li>Enhance user experience.</li>
//           <li>Analyze website traffic.</li>
//           <li>Remember your preferences.</li>
//         </ul>
//         <p className="mb-5 text-base md:text-lg">
//           You can manage cookie settings through your browser.
//         </p>

//         <h1 className="font-bold text-lg md:text-2xl my-8">
//           5. Data Security{" "}
//         </h1>
//         <p className="mb-5 text-base md:text-lg">
//           We implement appropriate technical and organizational measures to
//           protect your data from unauthorized access, loss, or misuse.
//         </p>

//         <h1 className="font-bold text-lg md:text-2xl my-8">6. Your Rights</h1>
//         <p className="mb-5 text-base md:text-lg">
//           Under UK GDPR, you have the right to:
//         </p>
//         <ul className="list-disc pl-5 space-y-2 mb-5 text-base md:text-lg">
//           <li>Access the personal data we hold about you.</li>
//           <li>Request correction or deletion of your data.</li>
//           <li>Object to or restrict certain processing.</li>
//           <li>Withdraw consent at any time.</li>
//         </ul>
//         <p className="mb-5 text-base md:text-lg">
//           To exercise your rights, contact us at support@zorqk.com.{" "}
//         </p>

//         <h1 className="font-bold text-lg md:text-2xl my-8">
//           7. Data Retention{" "}
//         </h1>
//         <p className="mb-5 text-base md:text-lg">
//           We retain your personal data only as long as necessary for the
//           purposes outlined in this policy or as required by law.
//         </p>

//         <h1 className="font-bold text-lg md:text-2xl my-8">
//           8. International Transfers{" "}
//         </h1>
//         <p className="mb-5 text-base md:text-lg">
//           If your data is transferred outside the UK, we ensure appropriate
//           safeguards are in place to protect your information.
//         </p>

//         <h1 className="font-bold text-lg md:text-2xl my-8">
//           9. Changes to This Policy{" "}
//         </h1>
//         <p className="mb-5 text-base md:text-lg">
//           We may update this Privacy Policy from time to time. We encourage you
//           to review it periodically.
//         </p>

//         <h1 className="font-bold text-lg md:text-2xl my-8">10. Contact Us</h1>
//         <p className="mb-5 text-base md:text-lg">
//           If you have any questions or concerns about this Privacy Policy,
//           please contact us at:{" "}
//           <a
//             href="mailto:support@zorqk.com"
//             className="text-blue-400 font-bold"
//           >
//             support@zorqk.com
//           </a>
//         </p>
//       </div>
//     </section>
//   );
// };

// export default PrivacyPolicy;
